export const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/** Legacy `capitalize(v, { lowerCaseRest: true })` — first letter upper, rest lower ('RETAIL' → 'Retail') */
export const capitalizeLower = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/** 'BROKER_CLIENT_SUBACCOUNT' → 'Broker client subaccount'; '_' and '-' become spaces */
export const sentenceCase = (value: string): string => capitalizeLower(value.replace(/[_-]+/g, ' ').trim());

// 'dateOfBirth' → 'date Of Birth'
export const splitCamelCase = (value: string): string => value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

// 'user-search' → 'User Search'
export const titleCase = (value: string): string => value.split('-').map(capitalize).join(' ');

/** 'Nathan Maybrey' → 'Nathan'; 'nathan.maybrey@onetrading.com' → 'Nathan'. '' when there is nothing usable. */
export const firstNameFrom = (value: string): string => {
  const [localPart = ''] = value.trim().split('@');
  const [firstToken = ''] = localPart.split(/[\s._-]+/).filter(Boolean);

  // Only fix the casing of email-derived tokens; 'McDonald' and 'JP' keep theirs
  return firstToken === firstToken.toLowerCase() ? capitalize(firstToken) : firstToken;
};

// Legacy formatUUID: first segment + ellipsis (full value stays available via copy/tooltip)
export const shortUuid = (value: string): string => `${value.split('-')[0]}…`;

/** 'abcdefghij' → 'abcde…' when over maxLength (single-char ellipsis, matching the shortUuid convention) */
export const truncateWithEllipsis = (value: string, maxLength: number): string => (value.length > maxLength ? `${value.slice(0, maxLength)}…` : value);

// 'login_from_prohibited_jurisdiction' → 'Login From Prohibited Jurisdiction'
export const formatSnakeCase = (value: string): string => value.split('_').map(capitalizeLower).join(' ');

/** `dateOfBirth` → `Date of birth`, from camel, snake or kebab case */
export const humanise = (value: string): string => {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return capitalize(words);
};

// Case-preserving underscore humanizer ('IN_PROGRESS' → 'IN PROGRESS') — deliberately
// distinct from formatSnakeCase above, which also title-cases every word.
export const underscoresToSpaces = (value: string): string => value.replace(/_/g, ' ');

// 'solidus/2026/08/02/report.csv' → 'report.csv'
export const extractFileName = (key: string): string => key.split('/').pop() || key;

const ISO_ALPHA2_PATTERN = /^[A-Z]{2}$/;
const REGIONAL_INDICATOR_OFFSET = 0x1f1e6;
const LETTER_A_CODE = 65;

// ' at ' → 'AT'
export const normalizeCountryCode = (value: string): string => value.trim().toUpperCase();

/** Shape check only (two uppercase letters) — does not prove the country exists. */
export const isIsoAlpha2 = (value: string): boolean => ISO_ALPHA2_PATTERN.test(value);

/** ISO-2 country code → regional-indicator flag emoji ('DE' → '🇩🇪'); non-ISO-2 input falls through raw. */
export const countryCodeToFlagEmoji = (code: string): string => {
  const upper = code.toUpperCase();
  if (!isIsoAlpha2(upper)) return code;
  return String.fromCodePoint(...[...upper].map((char) => REGIONAL_INDICATOR_OFFSET + (char.charCodeAt(0) - LETTER_A_CODE)));
};

/** pluralize(1, 'file') → '1 file'; pluralize(3, 'file') → '3 files' */
export const pluralize = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;
